import React, { useState } from 'react';
import { Plus, Trash2, DollarSign, Paperclip, ChevronDown, ChevronUp, ArrowUp, ArrowDown, GripVertical, X } from 'lucide-react';
import Input from '../ui/Input';
import Select from '../ui/Select';
import Button from '../ui/Button';
import Card from '../ui/Card';
import DeleteConfirmationModal from '../ui/DeleteConfirmationModal';
import RichTextEditor from '../ui/RichTextEditor';
import FilePicker from '../ui/FilePicker';
import SubTaskAttachmentList from '../ui/SubTaskAttachmentList';
import { useFieldArray, useFormContext, useWatch, Controller } from 'react-hook-form';
import { useAuth } from '@/hooks/useAuth';
import { subTaskService } from '@/services/subTaskService';
import toast from 'react-hot-toast';
import {
    DndContext,
    closestCenter,
    PointerSensor,
    KeyboardSensor,
    useSensor,
    useSensors,
    DragEndEvent,
} from '@dnd-kit/core';
import {
    SortableContext,
    sortableKeyboardCoordinates,
    verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import SortableListItem from '../ui/SortableListItem';

interface SubTask {
    id?: number;
    title: string;
    description: string;
    amount: string;
    excluded?: boolean;
}

interface FormData {
    subTasks: SubTask[];
}

interface SubTaskFormProps {
    taskId?: number;
}

const SubTaskForm: React.FC<SubTaskFormProps> = ({ taskId }) => {
    const { hasProfile } = useAuth();
    const isAdmin = hasProfile('ADMIN');
    const canViewValues = isAdmin;

    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [subTaskToDelete, setSubTaskToDelete] = useState<{ index: number; subTask: SubTask } | null>(null);
    const [isDeleting, setIsDeleting] = useState(false);
    const [expandedAttachments, setExpandedAttachments] = useState<Record<number, boolean>>({});
    const [pendingFiles, setPendingFiles] = useState<Record<number, File[]>>({});
    const [attachmentRefresh, setAttachmentRefresh] = useState<Record<number, number>>({});
    const [attachmentCounts, setAttachmentCounts] = useState<Record<number, number>>({});

    const {
        control,
        register,
        formState: { errors },
        setValue
    } = useFormContext<FormData>();

    const { fields, append, move } = useFieldArray({
        control,
        name: 'subTasks'
    });

    const watchSubTasks = useWatch({ control, name: 'subTasks' });

    const [collapsedCards, setCollapsedCards] = useState<Record<string, boolean>>({});

    const isCardCollapsed = (fieldId: string, idx: number): boolean => {
        if (collapsedCards[fieldId] !== undefined) return collapsedCards[fieldId];
        return !!watchSubTasks?.[idx]?.id;
    };

    const toggleCard = (fieldId: string, idx: number) => {
        const currentlyCollapsed = isCardCollapsed(fieldId, idx);
        setCollapsedCards(prev => ({ ...prev, [fieldId]: !currentlyCollapsed }));
    };

    const visibleFields = fields.filter((_, idx) => !watchSubTasks?.[idx]?.excluded);
    const anyExpanded = visibleFields.some(f => {
        const idx = fields.findIndex(ff => ff.id === f.id);
        return !isCardCollapsed(f.id, idx);
    });

    const setAllCollapsed = (collapsed: boolean) => {
        setCollapsedCards(prev => {
            const next = { ...prev };
            visibleFields.forEach(f => { next[f.id] = collapsed; });
            return next;
        });
    };

    const handleMoveUp = (index: number) => {
        if (index <= 0) return;
        move(index, index - 1);
    };

    const handleMoveDown = (index: number) => {
        if (index >= fields.length - 1) return;
        move(index, index + 1);
    };

    const sensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
        useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
    );

    const handleDragEnd = (event: DragEndEvent) => {
        const { active, over } = event;
        if (!over || active.id === over.id) return;
        const oldIndex = fields.findIndex(f => f.id === active.id);
        const newIndex = fields.findIndex(f => f.id === over.id);
        if (oldIndex !== -1 && newIndex !== -1) {
            move(oldIndex, newIndex);
        }
    };

    const calculateTotal = (): number => {
        if (!watchSubTasks) return 0;
        return watchSubTasks
            .filter((subTask) => !subTask?.excluded)
            .reduce((total, subTask) => {
                const amount = parseFloat(subTask?.amount) || 0;
                return total + amount;
            }, 0);
    };

    const addSubTask = (e?: React.MouseEvent): void => {

        if (e) {
            e.preventDefault();
            e.stopPropagation();
        }

        const currentScrollPosition = window.scrollY;
        
        append({
            title: '',
            description: '',
            amount: canViewValues ? '' : '0',
            excluded: false
        });

        setTimeout(() => {
            window.scrollTo(0, currentScrollPosition);
        }, 0);
    };

    const handleDeleteSubTask = (index: number): void => {
        const subTask = watchSubTasks?.[index];

        // Subtarefa nova (ainda não salva no backend) — remove direto, sem modal
        if (!subTask?.id) {
            const filtered = [...watchSubTasks];
            filtered.splice(index, 1);
            setValue('subTasks', filtered);
            return;
        }

        // Subtarefa persistida — exige confirmação (delete via API)
        setSubTaskToDelete({ index, subTask });
        setShowDeleteModal(true);
    };

    const confirmDeleteSubTask = async (): Promise<void> => {
        if (subTaskToDelete === null) return;

        const { index, subTask } = subTaskToDelete;

        try {
            setIsDeleting(true);

            if (subTask?.id) {

                await subTaskService.deleteSubTask(subTask.id);
                toast.success('Subtarefa excluída com sucesso');

                const filtered = [...watchSubTasks];
                filtered.splice(index, 1);
                setValue('subTasks', filtered);
            } else {

                const filtered = [...watchSubTasks];
                filtered.splice(index, 1);
                setValue('subTasks', filtered);
            }

            setShowDeleteModal(false);
            setSubTaskToDelete(null);
        } catch (error: any) {
            console.error('Erro ao excluir subtarefa:', error);
            toast.error(error?.response?.data?.message || 'Erro ao excluir subtarefa');
        } finally {
            setIsDeleting(false);
        }
    };


    const formatCurrency = (value: number): string => {
        return new Intl.NumberFormat('pt-BR', {
            style: 'currency',
            currency: 'BRL'
        }).format(value);
    };

    return (
        <div className="space-y-3">

            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex items-center gap-2 text-sm text-text-secondary">
                    <span className="font-medium text-text-primary">
                        {watchSubTasks?.filter(st => !st?.excluded).length || 0} subtarefa{(watchSubTasks?.filter(st => !st?.excluded).length || 0) === 1 ? '' : 's'}
                    </span>
                    {canViewValues && (
                        <span className="text-text-tertiary">
                            · Total <span className="font-semibold text-text-primary">{formatCurrency(calculateTotal())}</span>
                        </span>
                    )}
                </div>

                <div className="flex flex-wrap items-center gap-2">
                    {visibleFields.length > 0 && (
                        <Button
                            type="button"
                            size="sm"
                            variant="secondary"
                            onClick={(e) => { e.preventDefault(); setAllCollapsed(anyExpanded); }}
                            onMouseDown={(e) => e.preventDefault()}
                        >
                            {anyExpanded ? (
                                <><ChevronUp className="w-4 h-4 mr-1" />Colapsar todas</>
                            ) : (
                                <><ChevronDown className="w-4 h-4 mr-1" />Expandir todas</>
                            )}
                        </Button>
                    )}
                    <Button
                        type="button"
                        size="sm"
                        onClick={addSubTask}
                        onMouseDown={(e) => e.preventDefault()}
                    >
                        <Plus className="w-4 h-4 mr-1" />
                        Adicionar Subtarefa
                    </Button>
                </div>
            </div>

            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                <SortableContext items={visibleFields.map(f => f.id)} strategy={verticalListSortingStrategy}>
            <div className="space-y-4">
                {fields.map((field, index) => {
                    const subTask = watchSubTasks?.[index];
                    if (subTask?.excluded) return null;

                    const collapsed = isCardCollapsed(field.id, index);
                    const visiblePosition = visibleFields.findIndex(f => f.id === field.id);
                    const isFirstVisible = visiblePosition === 0;
                    const isLastVisible = visiblePosition === visibleFields.length - 1;

                    return (
                        <SortableListItem key={field.id} id={field.id}>
                          {({ attributes, listeners }) => (
                        <Card>
                            <div
                                className="flex items-center justify-between gap-2 cursor-pointer select-none"
                                onClick={() => toggleCard(field.id, index)}
                            >
                                <div className="flex items-center gap-2 min-w-0 flex-1">
                                    <button
                                        type="button"
                                        {...attributes}
                                        {...listeners}
                                        onClick={(e) => e.stopPropagation()}
                                        title="Arraste para reordenar"
                                        aria-label="Arrastar"
                                        className="p-1 rounded hover:bg-surface-2 text-text-tertiary cursor-grab active:cursor-grabbing touch-none shrink-0"
                                    >
                                        <GripVertical className="w-4 h-4" />
                                    </button>
                                    <span className="inline-flex items-center justify-center px-2 py-1 rounded-lg text-xs sm:text-sm font-bold bg-gradient-to-r from-blue-500 to-purple-600 text-white shrink-0">
                                        {visiblePosition + 1}
                                    </span>
                                    <span className="text-sm sm:text-base font-medium text-text-primary truncate" title={subTask?.title || ''}>
                                        {subTask?.title?.trim() || (subTask?.id ? `Subtarefa ${subTask.id}` : 'Nova subtarefa')}
                                    </span>
                                </div>

                                <div className="flex items-center gap-1 sm:gap-2 shrink-0">
                                    <button
                                        type="button"
                                        onClick={(e) => { e.stopPropagation(); handleMoveUp(index); }}
                                        disabled={isFirstVisible}
                                        title="Mover para cima"
                                        className="p-1 rounded hover:bg-surface-2 disabled:opacity-40 disabled:cursor-not-allowed text-text-secondary"
                                    >
                                        <ArrowUp className="w-4 h-4" />
                                    </button>
                                    <button
                                        type="button"
                                        onClick={(e) => { e.stopPropagation(); handleMoveDown(index); }}
                                        disabled={isLastVisible}
                                        title="Mover para baixo"
                                        className="p-1 rounded hover:bg-surface-2 disabled:opacity-40 disabled:cursor-not-allowed text-text-secondary"
                                    >
                                        <ArrowDown className="w-4 h-4" />
                                    </button>
                                    {canViewValues && watchSubTasks?.[index]?.amount && (
                                        <span className="text-sm sm:text-base font-bold text-green-600">
                                            {formatCurrency(parseFloat(watchSubTasks[index].amount) || 0)}
                                        </span>
                                    )}
                                    {subTask?.id ? (
                                        <Button
                                            type="button"
                                            size="sm"
                                            variant="danger"
                                            onClick={(e) => { e.stopPropagation(); handleDeleteSubTask(index); }}
                                            className="p-1.5 sm:p-2"
                                            title="Excluir subtarefa"
                                        >
                                            <Trash2 className="w-4 h-4" />
                                        </Button>
                                    ) : (
                                        <button
                                            type="button"
                                            onClick={(e) => { e.stopPropagation(); handleDeleteSubTask(index); }}
                                            title="Remover esta subtarefa"
                                            aria-label="Remover esta subtarefa"
                                            className="p-1.5 sm:p-2 rounded-md text-text-tertiary hover:text-[var(--danger-strong)] hover:bg-danger-soft transition-colors"
                                        >
                                            <X className="w-4 h-4" />
                                        </button>
                                    )}
                                    {collapsed ? (
                                        <ChevronDown className="w-5 h-5 text-text-tertiary" />
                                    ) : (
                                        <ChevronUp className="w-5 h-5 text-text-tertiary" />
                                    )}
                                </div>
                            </div>

                            {!collapsed && (
                                <div className="space-y-3 sm:space-y-4 mt-4 pt-4 border-t border-border-subtle">

                                    <div>
                                        <label className="block text-sm font-medium text-text-secondary mb-1.5 sm:mb-2">
                                            Título <span className="text-red-500">*</span>
                                        </label>
                                        <textarea
                                            {...register(`subTasks.${index}.title`)}
                                            rows={2}
                                            placeholder="Digite o título da subtarefa"
                                            className="w-full px-2.5 py-2 sm:px-3 border border-border-strong rounded-lg focus:ring-2 focus:ring-accent focus:border-accent resize-vertical text-sm sm:text-base"
                                            maxLength={200}
                                        />
                                        {errors.subTasks?.[index]?.title && (
                                            <p className="mt-1 text-xs sm:text-sm text-red-600">{errors.subTasks[index].title.message}</p>
                                        )}
                                    </div>


                                    <Controller
                                        name={`subTasks.${index}.description`}
                                        control={control}
                                        render={({ field }) => (
                                            <RichTextEditor
                                                label="Descrição"
                                                value={field.value || ''}
                                                onChange={field.onChange}
                                                placeholder="Descreva a subtarefa em detalhes (opcional). Você pode colar imagens diretamente..."
                                                error={errors.subTasks?.[index]?.description?.message}
                                                minHeight="150px"
                                                entityType="SUBTASK"
                                                entityId={subTask?.id}
                                                parentId={taskId}
                                            />
                                        )}
                                    />


                                    <div className="grid grid-cols-1 gap-4">
                                        {canViewValues ? (
                                            <div className="relative">
                                                <Input
                                                    {...register(`subTasks.${index}.amount`, {
                                                        valueAsNumber: false
                                                    })}
                                                    type="number"
                                                    step="0.01"
                                                    min="0"
                                                    label="Valor"
                                                    placeholder="0,00"
                                                    error={errors.subTasks?.[index]?.amount?.message}
                                                />
                                                <DollarSign className="absolute right-3 top-9 h-4 w-4 text-text-tertiary" />
                                            </div>
                                        ) : (

                                            <input
                                                type="hidden"
                                                {...register(`subTasks.${index}.amount`)}
                                                value="0"
                                            />
                                        )}

                                    </div>

                                    {subTask?.id && (
                                        <div className="mt-4 border-t pt-4">
                                            <div
                                                className="cursor-pointer border border-border-subtle rounded-lg"
                                                onClick={() => setExpandedAttachments(prev => ({
                                                    ...prev,
                                                    [subTask.id!]: !prev[subTask.id!]
                                                }))}
                                            >
                                                <div className="px-4 py-3 hover:bg-surface-app transition-colors">
                                                    <div className="flex items-center justify-between">
                                                        <div className="flex items-center gap-2">
                                                            <Paperclip className="w-4 h-4 text-text-tertiary" />
                                                            <span className="text-sm font-medium text-text-primary">Anexos</span>
                                                            {attachmentCounts[subTask.id!] > 0 && (
                                                                <span className="text-xs font-medium text-accent">({attachmentCounts[subTask.id!]})</span>
                                                            )}
                                                        </div>
                                                        <div className="flex items-center gap-2">
                                                            <span className="text-sm text-text-tertiary">
                                                                {expandedAttachments[subTask.id!] ? 'Recolher' : 'Expandir'}
                                                            </span>
                                                            {expandedAttachments[subTask.id!] ? (
                                                                <ChevronUp className="w-4 h-4 text-text-tertiary" />
                                                            ) : (
                                                                <ChevronDown className="w-4 h-4 text-text-tertiary" />
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Componente oculto para carregar contagem */}
                                            {!expandedAttachments[subTask.id!] && (
                                                <div className="hidden">
                                                    <SubTaskAttachmentList
                                                        subTaskId={subTask.id}
                                                        refreshTrigger={attachmentRefresh[subTask.id!] || 0}
                                                        onCountChange={(count) => setAttachmentCounts(prev => ({
                                                            ...prev,
                                                            [subTask.id!]: count
                                                        }))}
                                                    />
                                                </div>
                                            )}

                                            {expandedAttachments[subTask.id!] && (
                                                <div className="mt-4 p-4 border border-border-subtle rounded-lg bg-surface-app">
                                                    <div className="mb-4">
                                                        <p className="text-sm text-text-tertiary">
                                                            Faca upload de documentos, planilhas, imagens ou outros arquivos relacionados a subtarefa
                                                        </p>
                                                    </div>

                                                    <FilePicker
                                                        files={pendingFiles[subTask.id!] || []}
                                                        onFilesChange={(files) => setPendingFiles(prev => ({
                                                            ...prev,
                                                            [subTask.id!]: files
                                                        }))}
                                                        maxFiles={10}
                                                        maxFileSize={10}
                                                        subTaskId={subTask.id}
                                                        showUploadButton={true}
                                                        onUploadSuccess={() => {
                                                            setAttachmentRefresh(prev => ({
                                                                ...prev,
                                                                [subTask.id!]: (prev[subTask.id!] || 0) + 1
                                                            }));
                                                        }}
                                                    />

                                                    <div className="mt-4">
                                                        <SubTaskAttachmentList
                                                            subTaskId={subTask.id}
                                                            refreshTrigger={attachmentRefresh[subTask.id!] || 0}
                                                            forceExpanded={true}
                                                            onCountChange={(count) => setAttachmentCounts(prev => ({
                                                                ...prev,
                                                                [subTask.id!]: count
                                                            }))}
                                                        />
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            )}

                            <input
                                type="hidden"
                                {...register(`subTasks.${index}.excluded`)}
                            />
                        </Card>
                          )}
                        </SortableListItem>
                    );
                })}
            </div>
                </SortableContext>
            </DndContext>

            {/* Rodapé: total + adicionar (atalho pra evitar scroll quando lista é longa) */}
            {watchSubTasks?.filter(st => !st?.excluded).length > 0 && (
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between pt-3 border-t border-border-subtle">
                    <div className="text-sm text-text-secondary">
                        {canViewValues && (
                            <>
                                <span className="text-text-tertiary">Total:</span>{' '}
                                <span className="font-semibold text-text-primary tabular-nums">
                                    {formatCurrency(calculateTotal())}
                                </span>
                            </>
                        )}
                    </div>
                    <Button
                        type="button"
                        size="sm"
                        onClick={addSubTask}
                        onMouseDown={(e) => e.preventDefault()}
                    >
                        <Plus className="w-4 h-4 mr-1" />
                        Adicionar Subtarefa
                    </Button>
                </div>
            )}

            {watchSubTasks?.filter(st => !st?.excluded).length === 0 && (
                <Card className="text-center py-8">
                    <div className="text-text-tertiary">
                        <DollarSign className="w-12 h-12 mx-auto mb-4 text-text-tertiary" />
                        <p className="mb-4">Nenhuma subtarefa adicionada</p>
                        <Button
                            onClick={addSubTask}
                            onMouseDown={(e) => e.preventDefault()}
                            className="w-full sm:w-auto"
                        >
                            <Plus className="w-4 h-4 mr-2" />
                            Adicionar Primeira Subtarefa
                        </Button>
                    </div>
                </Card>
            )}

            <DeleteConfirmationModal
                isOpen={showDeleteModal}
                onClose={() => {
                    setShowDeleteModal(false);
                    setSubTaskToDelete(null);
                }}
                onConfirm={confirmDeleteSubTask}
                itemName={subTaskToDelete?.subTask?.title}
                description="Esta ação não pode ser desfeita."
                isDeleting={isDeleting}
            />
        </div>
    );
};

export default SubTaskForm;